import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Space, Typography, Input, Popconfirm, Empty, Spin, Result, Avatar, message } from 'antd';
import { ReloadOutlined, SendOutlined, CloseCircleOutlined, PaperClipOutlined, CustomerServiceOutlined, UserOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import AttachmentImages from '../../../shared/components/AttachmentImages';
import supportService from '../../services/supportService';
import { formatDateTime } from '../../../shared/utils/date';
import { userPath } from '../../../shared/config/paths';
import { uploadFiles, patchFile } from '../../../shared/utils/directUpload';
import { TicketStatusTag, PriorityTag, AttachmentPicker, STATUS_META, DEFAULT_LIMITS, serviceText, formatSize, ok } from './common';

const { Text } = Typography;
const POLL_MS = 30000;

const MessageBubble = ({ m, onDownload, downloading }) => {
    const { t } = useTranslation();
    const mine = m.author_type === 'user';
    return (
        <div style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 8, alignItems: 'flex-start', marginBottom: 16 }}>
            <Avatar
                size={32}
                icon={mine ? <UserOutlined /> : <CustomerServiceOutlined />}
                style={{ flexShrink: 0, background: mine ? 'var(--ant-color-primary, #1677ff)' : '#52c41a' }}
            />
            <div style={{ maxWidth: 'min(720px, calc(100% - 48px))', minWidth: 0, textAlign: mine ? 'right' : 'left' }}>
                <div style={{ fontSize: 12, marginBottom: 4 }}>
                    <Text strong style={{ fontSize: 12 }}>{mine ? t('Me') : t('Support')}</Text>
                    <Text type="secondary" style={{ fontSize: 12, marginInlineStart: 8 }}>{formatDateTime(m.created_at)}</Text>
                </div>
                <div
                    style={{
                        display: 'inline-block',
                        textAlign: 'left',
                        padding: '10px 14px',
                        borderRadius: 10,
                        background: mine ? 'var(--ant-color-primary-bg, #e6f4ff)' : 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.04))',
                        border: '1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.06))',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%',
                    }}
                >
                    {m.body}
                    <AttachmentImages items={m.attachments} />
                    {Array.isArray(m.attachments) && m.attachments.some((a) => !a.preview_url) && (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {m.attachments.filter((a) => !a.preview_url).map((a) => (
                                <Button
                                    key={a.id}
                                    type="link"
                                    size="small"
                                    icon={<PaperClipOutlined />}
                                    loading={downloading === a.id}
                                    onClick={() => onDownload(a)}
                                    style={{ padding: 0, height: 'auto', textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-all' }}
                                >
                                    {a.filename}
                                    <Text type="secondary" style={{ fontSize: 12, marginInlineStart: 6 }}>{formatSize(a.size)}</Text>
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TicketDetail = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [limits, setLimits] = useState(DEFAULT_LIMITS);
    const [body, setBody] = useState('');
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [closing, setClosing] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const bottomRef = useRef(null);
    const lastCount = useRef(0);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const r = await supportService.getTicket(id);
            if (ok(r)) {
                setTicket(r.data?.ticket || null);
                setMessages(Array.isArray(r.data?.messages) ? r.data.messages : []);
                setNotFound(false);
            } else if (!silent) {
                setNotFound(true);
                message.error(r?.msg || t('Failed to load'));
            }
        } catch (e) {
            if (!silent) message.error(e?.msg || t('Failed to load'));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [id, t]);

    useEffect(() => { load(); }, [load]);

    // 上传限制（与提交工单一致）
    useEffect(() => {
        supportService.getOptions()
            .then((r) => { if (ok(r) && r.data?.limits) setLimits({ ...DEFAULT_LIMITS, ...r.data.limits }); })
            .catch(() => {});
    }, []);

    // 轮询新回复（页面可见时）
    useEffect(() => {
        const timer = setInterval(() => {
            if (document.visibilityState === 'visible') load(true);
        }, POLL_MS);
        return () => clearInterval(timer);
    }, [load]);

    // 有新消息时滚到底部
    useEffect(() => {
        if (messages.length > lastCount.current && lastCount.current > 0 && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
        lastCount.current = messages.length;
    }, [messages.length]);

    const handleReply = async () => {
        if (!body.trim()) { message.warning(t('Please enter a reply')); return; }
        setSending(true);
        try {
            const attachments = await uploadFiles(files, supportService.getUploadUrl, (uid, patch) => setFiles((list) => patchFile(list, uid, patch)));
            const r = await supportService.reply(id, body.trim(), attachments);
            if (ok(r)) {
                setBody('');
                setFiles([]);
                message.success(t('Sent'));
                await load(true);
            } else {
                message.error(r?.msg || t('Send failed'));
            }
        } catch (e) {
            message.error(e?.msg || t('Send failed'));
        } finally {
            setSending(false);
        }
    };

    const handleClose = async () => {
        setClosing(true);
        try {
            const r = await supportService.closeTicket(id);
            if (ok(r)) {
                message.success(t('Ticket closed'));
                await load(true);
            } else {
                message.error(r?.msg || t('Operation failed'));
            }
        } catch (e) {
            message.error(e?.msg || t('Operation failed'));
        } finally {
            setClosing(false);
        }
    };

    const handleDownload = async (a) => {
        setDownloading(a.id);
        try {
            await supportService.downloadAttachment(a);
        } catch (e) {
            message.error(e?.msg || t('Download failed'));
        } finally {
            setDownloading(null);
        }
    };

    const back = () => navigate(userPath('/support/tickets'));

    if (!loading && (notFound || !ticket)) {
        return (
            <Result
                status="404"
                title={t('Ticket not found')}
                extra={<Button type="primary" onClick={back}>{t('Back')}</Button>}
            />
        );
    }

    const closed = ticket?.status === 'closed';
    const service = ticket ? serviceText(t, ticket.service_type, ticket.resource_label) : null;
    const hint = ticket && STATUS_META[ticket.status] ? STATUS_META[ticket.status].hint : null;

    return (
        <Spin spinning={loading && !ticket}>
            <PageHeader
                onBack={back}
                title={ticket ? ticket.subject : t('Ticket details')}
                tags={ticket && (
                    <Space size={4}>
                        <TicketStatusTag status={ticket.status} />
                        <PriorityTag priority={ticket.priority} />
                    </Space>
                )}
                subtitle={ticket && (
                    <Space size={[12, 2]} wrap split={<Text type="secondary">·</Text>}>
                        <span>#{ticket.number || ticket.id}</span>
                        {ticket.department_name && <span>{t(ticket.department_name)}</span>}
                        {service && <span>{service}</span>}
                        <span>{formatDateTime(ticket.created_at)}</span>
                    </Space>
                )}
                extra={(
                    <>
                        <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading && !!ticket}>{t('Refresh')}</Button>
                        {ticket && !closed && (
                            <Popconfirm title={t('Close this ticket?')} onConfirm={handleClose} okText={t('OK')} cancelText={t('Cancel')}>
                                <Button icon={<CloseCircleOutlined />} loading={closing}>{t('Close ticket')}</Button>
                            </Popconfirm>
                        )}
                    </>
                )}
            />

            {hint && ticket.status !== 'closed' && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>{t(hint)}</Text>
            )}

            <Card bordered={false} style={{ marginBottom: 16 }}>
                {messages.length === 0
                    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    : messages.map((m) => (
                        <MessageBubble key={m.id} m={m} onDownload={handleDownload} downloading={downloading} />
                    ))}
                <div ref={bottomRef} />
            </Card>

            {ticket && (
                <Card bordered={false} title={t('Reply')} size="small">
                    <Input.TextArea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        autoSize={{ minRows: 4, maxRows: 12 }}
                        maxLength={20000}
                        disabled={sending}
                        placeholder={closed ? t('Replying will reopen this ticket') : undefined}
                        onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleReply(); }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                            <AttachmentPicker value={files} onChange={setFiles} limits={limits} disabled={sending} />
                        </div>
                        <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={handleReply}>
                            {closed ? t('Reply & reopen') : t('Send')}
                        </Button>
                    </div>
                </Card>
            )}
        </Spin>
    );
};

export default TicketDetail;
